import type {
  VisitCounselView,
  RoomObservingSession,
  SketchingSessionRoom,
  AdditionalSpaceObservingSession,
} from '@/entities/visit-counsel/model/visit-counsel.types';
import { VisitCounselState } from '@/entities/visit-counsel/model/visit-counsel.types';
import {
  SERVICE_SET_VALUES,
  type MovingItemRequest,
  type ServiceSet,
} from '@/entities/visit-counsel/model/room-observing.types';
import { hasObservingSessionMovingItemsConfirmed } from '@/entities/visit-counsel/model/observing-session-moving-items';
import { resolveRoomObservingSessionForSketchRoom } from '@/entities/visit-counsel/model/resolveRoomObservingSessionForSketchRoom';
import {
  formatSketchingRoomLabelSpaced,
  resolveRoomObservingDisplayLabelSpaced,
  formatRoomObservingLeafPrefix,
} from '@/entities/visit-counsel/model/sketchRoomDisplayLabel';
import { isSingletonRoomType } from '@/entities/visit-counsel/model/recoverRoomType';
import type { RoomTypeValue } from '@/entities/visit-counsel/model/house-sketch.types';
import {
  roomTypeFromSketchRoom,
  roomTypeFromSpaceType,
} from '@/features/visit-counsel/moving-items-flow/model/resolveObservingRoomType';
import { isFormalAdditionalSpaceType } from '@/entities/visit-counsel/model/formalAdditionalSpace';
import { hasCompletedMovingTypesProgressStep } from '@/entities/visit-counsel/model/intake-helpers';
import { SPACE_TYPE_LABELS } from '@/features/visit-counsel/counsel-progress/model/counsel-progress.constants';
import { sortAdditionalSpaceObservingSessions } from '@/features/visit-counsel/counsel-progress/model/additionalSpaceObservingOrder';

// ── Types ────────────────────────────────────────────────────────────────────

/**
 * 상담 진행하기 단계의 sub-stage.
 *
 * Figma 의 "상담 진행하기 플로우" 메인 시퀀스를 counsel 스냅샷에 따라 미완료 step
 * 으로 자동 분기한다. 각 sub-stage는 자체 InquiryFlow 인스턴스 + 저장 API 를 가진
 * 어댑터로 매핑된다 (ProgressFlowAdapter 가 dispatcher).
 *
 * - `moving-date`        — 이사일자(`movingDate`) 미입력
 * - `departure-transport`— 출발지 운반방식(`departureMovingMethod`) 미저장
 * - `arrival-address`    — 도착지 주소(`arrivalAddress`) 미저장
 * - `arrival-transport`  — 도착지 운반방식(`arrivalMovingMethod`) 미저장
 * - `packing`            — 포장방식(`packing`) 미저장
 * - `moving-types`       — 이사 유형 화면 미완료 (PUT moving-type 미호출·빈 API 기본값만 있는 경우 포함)
 * - `customer-requests`  — 추가 요청사항(`customerRequests`) 미저장
 * - `completed`          — intake-session 모두 채워짐. 다음 호출에서 stage='done'
 */
export type ProgressSubStage =
  | { kind: 'moving-date' }
  | { kind: 'departure-transport' }
  | { kind: 'arrival-address' }
  | { kind: 'arrival-transport' }
  | { kind: 'packing' }
  | { kind: 'moving-types' }
  | { kind: 'customer-requests' }
  | { kind: 'completed' };

/**
 * 통합 InquiryFlow 진입 페이지(`CounselStartPage`)에서 다음에 보여줄 단계.
 *
 * 한 페이지가 단계별 어댑터(CounselStartFlow, HouseStructure, MovingItems,
 * Progress)를 순차적으로 렌더하기 위해 사용된다.
 *
 * - `start`            — counsel.state === APPOINTED. 출발지 입력부터 시작.
 * - `house-structure`  — STARTED + sketching 세션 없음. 방 구조 기록만.
 * - `pick-room`        — STARTED + sketching 있음 + 현관 짐 기록 완료 +
 *                         미완료 방/추가공간이 2개 이상 남음. 사용자가 다음 진행할
 *                         방을 직접 선택한다 (RoomObservingFlowAdapter).
 * - `moving-items`     — STARTED + sketching 있음 + 자동으로 진행할 한 세션이
 *                         결정된 경우(현관 미완료, 또는 미완료가 1개 남은 경우).
 * - `progress`         — 모든 방·추가공간 짐 기록 완료. "상담 진행하기" 시퀀스 진입.
 *                         `sub`에 따라 이사일/출발지·도착지 운반/주소/포장/이사유형/요청 step 을
 *                         자동 분기 (ProgressFlowAdapter).
 * - `sketch-video`     — "상담 진행하기" sub 모두 완료 + 집 스케치 영상 미촬영.
 *                         페이지는 먼저 "상담 현황 리뷰" 화면을 한 번 보여준 뒤(post-progress
 *                         review), 사용자 CTA 로 본 스케치 영상 촬영 어댑터에 진입한다
 *                         (SketchVideoStepAdapter). 옛 ProgressPage 의 "집 스케치 영상" 항목
 *                         포지션과 동일.
 * - `additional-spaces`— sub + 스케치 영상 완료 + 추가공간 촬영(영상/짐) 미완료.
 *                         사용자 동의 후 추가공간 촬영 어댑터로 진입한다
 *                         (AdditionalSpaceFlowAdapter). 백엔드가 운반방식/sketching 시점에
 *                         자동 생성한 LADDER_TRUCK_ENTRANCE / STAIRS / RESIDENCE_STORAGE 등을
 *                         촬영한다.
 * - `done`             — 짐 기록 + 진행 단계 + 스케치 영상 + 추가공간 촬영까지 모두 완료.
 *                         완료 화면 진입 (또는 다른 누락 항목이 있으면 페이지가 상담 상세로 폴백).
 */
export type CounselFlowStage =
  | { kind: 'start' }
  | { kind: 'house-structure' }
  | { kind: 'pick-room' }
  | { kind: 'moving-items'; target: CounselFlowStageMovingItemsTarget }
  | { kind: 'progress'; sub: ProgressSubStage }
  | { kind: 'sketch-video' }
  | { kind: 'additional-spaces' }
  | { kind: 'done' };

export interface CounselFlowStageMovingItemsTarget {
  /** RoomObservingSession 또는 AdditionalSpaceObservingSession 의 id */
  sessionId: string;
  isAdditionalSpaceMoving: boolean;
  /** 세션이 REQUESTED 상태(짐 기록을 시작하지 않음) */
  isFirstStart: boolean;
  roomLabel: string;
  roomType?: RoomTypeValue;
  initialMovingItems: MovingItemRequest[] | undefined;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function toMovingItemRequests(
  session: RoomObservingSession | AdditionalSpaceObservingSession
): MovingItemRequest[] | undefined {
  const items = session.movingItems;
  if (!items || items.length === 0) return undefined;
  const mapped = items
    .filter((it) => SERVICE_SET_VALUES.includes(it.serviceSet as ServiceSet))
    .map((it) => ({
      specId: it.specId,
      attributes: it.attributes ?? {},
      serviceSet: it.serviceSet as ServiceSet,
    }));
  return mapped.length > 0 ? mapped : undefined;
}

function buildPrimaryRoomTarget(
  room: SketchingSessionRoom,
  rooms: SketchingSessionRoom[],
  roomObserving: RoomObservingSession[]
): CounselFlowStageMovingItemsTarget | null {
  const session = resolveRoomObservingSessionForSketchRoom(room, rooms, roomObserving);
  if (!session) return null;
  if (hasObservingSessionMovingItemsConfirmed(session)) return null;

  const singleton = isSingletonRoomType(room, rooms);
  const labelOpts = singleton ? { singleton: true as const } : undefined;
  const fromApi = resolveRoomObservingDisplayLabelSpaced(session, rooms, labelOpts);
  const roomLabel =
    fromApi ??
    formatRoomObservingLeafPrefix(
      session,
      rooms,
      {
        index: room.index,
        namePrefix: room.namePrefix,
      },
      labelOpts
    ) ??
    formatSketchingRoomLabelSpaced(room, labelOpts);

  return {
    sessionId: session.id,
    isAdditionalSpaceMoving: false,
    isFirstStart: session.status === 'REQUESTED',
    roomLabel,
    roomType: roomTypeFromSketchRoom(room),
    initialMovingItems: toMovingItemRequests(session),
  };
}

function buildAdditionalSpaceTarget(
  session: AdditionalSpaceObservingSession
): CounselFlowStageMovingItemsTarget | null {
  if (session.canRecordMovingItems !== true) return null;
  if (hasObservingSessionMovingItemsConfirmed(session)) return null;
  const roomLabel = SPACE_TYPE_LABELS[session.spaceType] ?? session.spaceType;
  return {
    sessionId: session.id,
    isAdditionalSpaceMoving: true,
    isFirstStart: session.status === 'REQUESTED',
    roomLabel,
    roomType: roomTypeFromSpaceType(session.spaceType),
    initialMovingItems: toMovingItemRequests(session),
  };
}

// ── 단계 판정 조각 (스테이지 규칙 DSL 과 공유) ────────────────────────────────
//
// 아래 함수들은 resolveCounselFlowStage 의 판정부를 그대로 잘라낸 것이다.
// `counselStageMachine.ts` 의 named predicate 가 같은 함수를 호출하도록 export 해서,
// 규칙을 문서로 옮기는 동안에도 도메인 로직이 한 곳에만 존재하도록 한다.

/** sketching 세션이 방 구조를 담고 있는지. false 면 house-structure 단계. */
export function hasSketchingData(counsel: VisitCounselView | null | undefined): boolean {
  const sketching = counsel?.sessions?.sketching ?? null;
  return (
    !!sketching &&
    (sketching.status === 'STARTED' ||
      sketching.status === 'COMPLETE' ||
      (sketching.rooms?.length ?? 0) > 0)
  );
}

/** 짐 기록이 끝나지 않은 첫 번째 현관(ENTRANCE) 세션의 진행 대상. 없으면 null. */
export function resolveEntranceTarget(
  counsel: VisitCounselView | null | undefined
): CounselFlowStageMovingItemsTarget | null {
  if (!counsel) return null;
  const sketchRooms = counsel.sessions?.sketching?.rooms ?? [];
  const roomObserving = counsel.sessions?.roomObserving ?? [];
  for (const room of sketchRooms.filter((r) => !r.isAdditionalSpace)) {
    if (room.type !== 'ENTRANCE') continue;
    const target = buildPrimaryRoomTarget(room, sketchRooms, roomObserving);
    if (target) return target;
  }
  return null;
}

/** 현관을 제외한 미완료 방·추가공간 진행 대상 목록. */
export function resolveRemainingTargets(
  counsel: VisitCounselView | null | undefined
): CounselFlowStageMovingItemsTarget[] {
  if (!counsel) return [];
  const sketchRooms = counsel.sessions?.sketching?.rooms ?? [];
  const roomObserving = counsel.sessions?.roomObserving ?? [];
  const remainingTargets: CounselFlowStageMovingItemsTarget[] = [];

  for (const room of sketchRooms.filter((r) => !r.isAdditionalSpace)) {
    if (room.type === 'ENTRANCE') continue;
    if (room.isAdditionalSpace !== false && isFormalAdditionalSpaceType(room.type)) continue;
    const target = buildPrimaryRoomTarget(room, sketchRooms, roomObserving);
    if (target) remainingTargets.push(target);
  }

  const additional = sortAdditionalSpaceObservingSessions(
    counsel.sessions?.additionalSpaceObserving ?? []
  );
  for (const session of additional) {
    if (session.isAdditionalSpace !== false && isFormalAdditionalSpaceType(session.spaceType))
      continue;
    const target = buildAdditionalSpaceTarget(session);
    if (target) remainingTargets.push(target);
  }

  return remainingTargets;
}

// ── Resolver ─────────────────────────────────────────────────────────────────

/**
 * 통합 진입 페이지가 다음에 렌더할 단계를 counsel 스냅샷 기반으로 결정한다.
 *
 * 단계 우선순위:
 *   start → house-structure → 현관(ENTRANCE) 짐 기록 → 미완료 1개면 자동 진행 →
 *   2개 이상이면 pick-room → 모두 완료 시 done
 *
 * - 현관(ENTRANCE)이 미완료면 항상 우선 자동 진행한다 — 사용자 요청: "현관은 미리
 *   짐기록/영상촬영 후에 나머지 방에 대해서만 해당 플로우로 진입".
 * - 현관 완료 후, 미완료 항목이 정확히 1개면 자동 진행한다 (선택할 게 없음).
 * - 2개 이상 남으면 사용자가 다음 진행할 방을 직접 선택할 수 있도록 `pick-room`을 반환.
 *
 * 한 단계의 어댑터가 완료되면 페이지는 refetch로 새 counsel을 받아 본 함수를
 * 다시 호출, 다음 단계로 자연스럽게 전환된다.
 */
export function resolveCounselFlowStage(
  counsel: VisitCounselView | null | undefined
): CounselFlowStage {
  if (!counsel || counsel.state !== VisitCounselState.STARTED) {
    return { kind: 'start' };
  }

  if (!hasSketchingData(counsel)) return { kind: 'house-structure' };

  // ── 1) 현관(ENTRANCE) 미완료가 있으면 우선 자동 진행 ────────────────────────
  const entranceTarget = resolveEntranceTarget(counsel);
  if (entranceTarget) return { kind: 'moving-items', target: entranceTarget };

  // ── 2) ENTRANCE 외 미완료 후보 수집 ───────────────────────────────────────
  // 공식 추가공간 (BALCONY/RESIDENCE_STORAGE) 은 pre-progress 후보에서 제외 —
  // `progress` 7-stage 가 끝난 뒤 `additional-spaces` 단계에서 별도 picker 로 촬영한다
  // (`formalAdditionalSpace.ts` 정책). 운반 경로(canRecordMovingItems=false) 도 기존과
  // 같이 `buildAdditionalSpaceTarget` 가 null 을 반환해 후보에서 빠진다 — 둘 다 결과적으로
  // `hasPendingAdditionalSpaceWork` 가 잡아 post-progress 의 picker 에서 노출.
  const remainingTargets = resolveRemainingTargets(counsel);

  // ENTRANCE 가 아닌 미완료가 1개 이상이면 항상 picker (`pick-room`) 로 보낸다.
  // 정책 (2025-05): 사용자가 "이어하기" 또는 picker 진입 후 뒤로가기 시 항상 picker 로
  // 복귀해야 한다 — 1개 자동 진입 시 뒤로가기가 상담 상세로 빠지는 회귀를 방지.
  // ENTRANCE 의 자동 우선 진행(§1) 은 그대로 유지한다.
  if (remainingTargets.length >= 1) {
    return { kind: 'pick-room' };
  }

  // ── 3) 모든 방·추가공간 짐 기록 완료 → "상담 진행하기" 시퀀스 진입 ─────────
  // counsel 스냅샷에 따라 미완료 sub-stage 를 자동 분기.
  const sub = resolveProgressSubStage(counsel);
  if (sub.kind === 'completed') {
    // 진행 sub-stage 모두 완료 → 사용자에게 먼저 "상담 현황 리뷰" 1회 노출 후
    // 다음 순서로 진행한다 (CounselStartPage 가 post-progress review 분기 처리):
    //   ① 집 스케치 영상 미촬영      → `sketch-video`
    //   ② 추가공간 (영상/짐) 미완료  → `additional-spaces`
    //   ③ 둘 다 완료                 → `done`
    // 스케치 영상이 먼저인 이유 — 옛 ProgressPage 의 체크리스트 순서(HOUSE_SKETCH 가
    // ADDITIONAL_SPACE 보다 우선)와 일치시키기 위함. 추가공간 picker 는 백엔드가 운반방식
    // 저장 시 자동 생성한 세션에 의존하는데, 스케치 영상 완료 전에는 picker 에 그
    // 세션들이 노출되지 않도록 했던 옛 동작 (`buildCounselChecklist` 의
    // `hasSketchVideo && sortedAdditionalSpaceObserving.length > 0` 가드) 과도 정렬.
    if (!hasSketchVideoRecorded(counsel)) {
      return { kind: 'sketch-video' };
    }
    if (hasPendingAdditionalSpaceWork(counsel)) {
      return { kind: 'additional-spaces' };
    }
    return { kind: 'done' };
  }
  return { kind: 'progress', sub };
}

/**
 * 집 스케치 영상이 이미 기록되었는지.
 *
 * `sessions.sketching.video` 토큰의 유무로 판정. sketching session 자체가 없는
 * (house-structure stage 이전) 케이스는 본 resolver 가 그 이전 분기에서 잡으므로
 * 호출되지 않는다.
 */
export function hasSketchVideoRecorded(counsel: VisitCounselView): boolean {
  return !!counsel.sessions?.sketching?.video;
}

/**
 * 추가공간 옵저빙에 미완료 작업(영상 또는 짐 기록)이 있는지.
 *
 * - `canRecordMovingItems === false` (사다리차/계단/엘리베이터 등): 영상만 필수.
 * - `canRecordMovingItems === true` (세대창고 등): 짐 기록 + serviceSet 누락 없음 + 영상 필수.
 *
 * (`isCounselReadyForCompletion` 의 추가공간 검사 로직과 정렬되어 있다 — 본 함수가 false 인데
 *  완료 화면이 막히면 다른 누락(메인 방/스케치 영상 등) 이므로 페이지가 상담 상세로 폴백한다.)
 */
export function hasPendingAdditionalSpaceWork(
  counsel: VisitCounselView | null | undefined
): boolean {
  if (!counsel) return false;
  const additional = counsel.sessions?.additionalSpaceObserving ?? [];
  for (const space of additional) {
    if (!space.video) return true;
    if (space.canRecordMovingItems !== true) continue;
    if (!hasObservingSessionMovingItemsConfirmed(space)) return true;
    const items = space.movingItems ?? [];
    for (const it of items) {
      if (it.serviceSet == null) return true;
    }
  }
  return false;
}

// ── Progress sub-stage resolver ──────────────────────────────────────────────

/**
 * counsel 스냅샷에서 다음에 진행할 "상담 진행하기" sub-stage 를 결정.
 *
 * 우선순위(Figma 시퀀스 그대로):
 *   1. 이사일 → 2. 출발지 운반 → 3. 도착지 주소 → 4. 도착지 운반 →
 *   5. 포장 → 6. 이사유형(item-picker) → 7. 추가요청 → completed
 *
 * 두집이사 / 양일이사 같은 분기 step 은 후속 작업으로 미루고 (recordMovingTypeIntake
 * attributes 또는 별도 API 가 정해지면 추가). 본 resolver 는 Figma 메인 시퀀스의
 * 백엔드 contract 와 1:1 매핑되는 단계만 다룬다.
 */
export function resolveProgressSubStage(counsel: VisitCounselView): ProgressSubStage {
  if (!counsel.movingDate) return { kind: 'moving-date' };
  if (!counsel.departureMovingMethod) return { kind: 'departure-transport' };
  if (!counsel.arrivalAddress) return { kind: 'arrival-address' };
  if (!counsel.arrivalMovingMethod) return { kind: 'arrival-transport' };
  if (!counsel.packing) return { kind: 'packing' };
  if (!hasCompletedMovingTypesProgressStep(counsel)) return { kind: 'moving-types' };
  if (!hasCustomerRequestsRecorded(counsel)) return { kind: 'customer-requests' };
  return { kind: 'completed' };
}

function hasCustomerRequestsRecorded(counsel: VisitCounselView): boolean {
  const reqs = counsel.customerRequests;
  if (!reqs) return false;
  // customerRequests 는 빈 배열이라도 "기록되었음"으로 간주 (사용자가 "없음" 선택 가능).
  // 백엔드가 null 이 아닌 객체를 반환했는지로 판정.
  return Array.isArray(reqs.questions) && Array.isArray(reqs.paidQuoteRequests);
}
